import c from 'classnames';
import { uniqBy } from 'lodash-es';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { urls } from '@repo/app-constants';
import { Kit } from '@repo/types';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { TinyPlusUpsellBanner } from 'components/TinyPlusUpsellBanner/TinyPlusUpsellBanner';
import { FeedbackPageType } from 'types/Feedback';
import {
  BacteriumType,
  GutVaginalHealthStrainKits,
  InsightMetric,
  ReportTaxaCategories,
} from 'types/Insights';

import { Icon, Typography } from '@repo/ui';
import {
  Accordion,
  Bacterium,
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  Card,
  ExpandableList,
  FAQ,
  FeedbackWidget,
  LinkButton,
  Markdown,
  TextLink,
} from 'components';
import SubMetricGraphs from '../SubMetricGraphs/SubMetricGraphs';

import AboutSection from './AboutSection';
import RelatedMetrics from './RelatedMetrics';
import ActionsReminderCard from './ActionsReminderCard';
import styles from './SubMetricData.module.css';
import AdditionalResources from './AdditionalResources';

const taxaFromReport = (
  report: ReportTaxaCategories,
  isVaginal: boolean,
): BacteriumType => {
  return {
    id: report.taxonomy_results?.id ?? '',
    rtc_id: report.id,
    taxonomy_name: report.taxonomy_name,
    classified_relative_abundance:
      report.taxonomy_results?.classified_relative_abundance,
    report_taxa_categories: report,
    about: isVaginal ? report.about_vaginal : report.about_gut,
    category: isVaginal ? report.category_vaginal : report.category_gut,
  };
};

const filterAndSortSpecies = (species: BacteriumType[]) => {
  return species
    .filter(specie => !!specie.classified_relative_abundance)
    .sort((a, b) => {
      return (
        (b.classified_relative_abundance ?? 0) -
        (a.classified_relative_abundance ?? 0)
      );
    });
};

const SPECIES_EVALUATION_KEY: { label: string; swatchClass?: string }[] = [
  { label: 'Disruptive', swatchClass: styles.swatchDisruptive },
  { label: 'Beneficial', swatchClass: styles.swatchBeneficial },
  { label: 'Variable', swatchClass: styles.swatchVariable },
  { label: 'Unknown', swatchClass: styles.swatchUnknown },
];

const SpeciesEvaluationKey = () => {
  return (
    <div className={styles.speciesKey}>
      <Typography variant='label' className={styles.speciesKeyTitle}>
        KEY:
      </Typography>
      <ul className={styles.speciesKeyList}>
        {SPECIES_EVALUATION_KEY.map(({ label, swatchClass }) => (
          <li key={label} className={styles.speciesKeyItem}>
            <span
              className={c(styles.swatch, swatchClass)}
              aria-hidden='true'
            />
            <Typography variant='label' className={styles.speciesKeyLabel}>
              {label}
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  );
};

const StrainRow = ({ brand }: { brand: string }) => {
  return (
    <div className='flex align-items-center gap-1'>
      <Icon name='check' size='s' className={styles.greyIcon} />
      <Typography variant='label' className={styles.greyText}>
        {brand}
      </Typography>
    </div>
  );
};

const StrainsDetected = ({
  strains,
}: {
  strains?: GutVaginalHealthStrainKits[];
}) => {
  if (!strains?.length)
    return (
      <div className={styles.strainsContainer}>
        <div className='flex align-items-center gap-3'>
          <Icon name='clipboardX' size='m' className={styles.redIcon} />
          <Typography variant='body-s'>{`We didn't find any specific strains from our database in your sample.`}</Typography>
        </div>
      </div>
    );

  const groupedStrains = strains.reduce(
    (
      grouped: { [key: string]: GutVaginalHealthStrainKits[] },
      strain: GutVaginalHealthStrainKits,
    ) => {
      const categoryKey = strain?.strain_name;
      if (categoryKey && grouped[categoryKey]) {
        grouped[categoryKey]!.push(strain);
      } else if (categoryKey) {
        grouped[categoryKey] = [strain];
      }
      return grouped;
    },
    {},
  );

  return (
    <div className={styles.strainsContainer}>
      {Object.entries(groupedStrains).map(([strainName, groupOfBrands]) => {
        return (
          <div key={`card_${strainName}`}>
            <div className='flex flex-column gap-2'>
              <Typography variant='body-s'>{strainName}</Typography>
              {!!groupOfBrands.filter(brand => !!brand.probiotic_brand)
                .length && (
                <div className='flex flex-column gap-2'>
                  {groupOfBrands.map(
                    brand =>
                      !!brand.probiotic_brand && (
                        <StrainRow
                          brand={brand.probiotic_brand}
                          key={strainName + '_' + brand.probiotic_brand}
                        />
                      ),
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

type SubMetricDataProps = {
  metric: InsightMetric;
  relatedActionsQty: number;
  refAP: React.RefObject<HTMLElement>;
  kit?: Kit | null;
};
export const SubMetricData = ({
  metric,
  relatedActionsQty,
  refAP,
  kit,
}: SubMetricDataProps) => {
  const { kitId } = useParams<{ kitId: string }>();
  const { trackEvent } = useAnalytics();

  const isVaginal = useMemo(
    () => metric?.sampling_type === 'vaginal',
    [metric],
  );

  const relatedSpecies: BacteriumType[] = useMemo(() => {
    const species: BacteriumType[] = [];
    const reports = metric?.gut_vaginal_health?.report_taxa_categories;
    if (reports && reports.length) {
      reports.forEach(report => {
        species.push(taxaFromReport(report, isVaginal));
      });
      return filterAndSortSpecies(species);
    }
    return [];
  }, [metric, isVaginal]);

  const showStrainSection =
    !!metric.include_strains && !!metric.sample_qc?.strain_completed;
  const hasToxins = !!metric.gut_vaginal_health_function_kits?.length;
  if (!metric || !kitId) return <></>;
  return (
    <div className={styles.subMetricData}>
      <ActionsReminderCard
        relatedActionsQty={relatedActionsQty}
        scrollTargetRef={refAP}
      />
      <div className='flex flex-column gap-3'>
        <SubMetricGraphs metric={metric} />
        <TinyPlusUpsellBanner kit={kit} />
      </div>

      <AboutSection metric={metric} />

      <RelatedMetrics metric={metric} />

      <AdditionalResources guides={metric.gut_vaginal_health.guides} />

      {!!metric?.faq && (
        <div className='pb-4'>
          <Typography variant='heading-l' className={styles.detailHeading}>
            Frequently asked questions
          </Typography>
          <FAQ faq={metric.faq} hideLastDivider />
        </div>
      )}

      {!!metric?.gut_vaginal_health?.id && (
        <div className={c(styles.feedbackWidget, 'hide-on-mobile')}>
          <FeedbackWidget
            kitId={kitId}
            pageType={FeedbackPageType.INSIGHT}
            pageId={metric.gut_vaginal_health.id}
          />
        </div>
      )}
      {!!relatedSpecies?.length && (
        <div className={styles.speciesSection}>
          <div className={styles.speciesSection}>
            <Typography variant='heading-m'>Species in your sample</Typography>
            <SpeciesEvaluationKey />
          </div>
          <Card>
            <div className={styles.bacteriumCard}>
              <ExpandableList
                max={4}
                onExpand={() =>
                  trackEvent('Species List Expanded', {
                    metric_name: metric?.name,
                  })
                }
              >
                {relatedSpecies.map((bacterium, i) => (
                  <Bacterium
                    key={bacterium.rtc_id || i}
                    bacterium={bacterium}
                    dotPosition='name'
                  />
                ))}
              </ExpandableList>
            </div>
          </Card>
        </div>
      )}
      {showStrainSection && (
        <div>
          <div className='flex flex-column gap-1'>
            <Typography variant='heading-m'>Strains detected</Typography>
            <Typography variant='body-s' className={styles.greyText}>
              These specific strains within this species were identified. For
              more information, see our{' '}
              <LinkButton
                label='FAQ'
                href={
                  'https://www.tinyhealth.com/blog/tiny-health-strain-tracking-faq'
                }
                external
                variant={ButtonVariants.TEXT}
                size={ButtonSizes.M}
                color={ButtonColors.PURPLE}
                className={styles.link}
              />
              .
            </Typography>
          </div>
          <StrainsDetected strains={metric.gut_vaginal_health_strain_kits} />
        </div>
      )}
      {!!metric?.show_toxins && (
        <div className='flex flex-column gap-3'>
          <Typography variant='heading-m'>Toxin genes</Typography>

          <Typography variant='body-s' className={styles.greyText}>
            These are notable toxin genes detected within the microbes in your
            sample. This measures the ability of the microbes to produce these
            toxins. It does not measure toxins directly.{' '}
            <TextLink
              label='See our FAQs.'
              to={urls.TOXIN_FAQ}
              size='body-s'
              external
            />{' '}
            If you are experiencing symptoms, we recommend talking to your
            practitioner or a microbiome specialist.
          </Typography>

          <div className={styles.geneTrackingCard}>
            {hasToxins ? (
              uniqBy(
                metric.gut_vaginal_health_function_kits,
                gvhfk => gvhfk.gut_vaginal_health_function?.gene_name,
              ).map(gvhfk => {
                return (
                  <Accordion
                    key={gvhfk.id}
                    header={gvhfk.gut_vaginal_health_function?.gene_name ?? ''}
                    initialState={false}
                    className={styles.geneTrackingAccordion}
                  >
                    <Markdown>
                      {gvhfk.gut_vaginal_health_function?.about ?? ''}
                    </Markdown>
                  </Accordion>
                );
              })
            ) : (
              <div className='flex gap-2 p-3'>
                <Icon name='clipboardX' className='red' />
                <Typography variant='body-s'>
                  We didn&apos;t find any notable genes in your sample.
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
